import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { paths } from '../../routes/paths';
import { CommissionAdjustmentDialog } from './CommissionAdjustmentDialog';
import {
  commissionLedgerRepositoryToken,
  defaultCommissionLedgerListQuery,
  hasMockAdjustments,
} from './repository';
import type {
  CommissionLedgerEntry,
  CommissionLedgerFilters,
  CommissionLedgerListQuery,
} from './types';
import { formatExactSignedMoney } from './types';

export function CommissionLedgerPage({
  adjustmentDataSource,
  dataSource,
}: {
  readonly adjustmentDataSource: DataSource;
  readonly dataSource: DataSource;
}) {
  const repository = useRepository(commissionLedgerRepositoryToken);
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [query, setQuery] = useState(() => initialQuery(searchParams));
  const [cursorHistory, setCursorHistory] = useState<readonly string[]>(['']);
  const [pageIndex, setPageIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.list(query, signal),
    [query, refreshKey, repository],
    (page) => page.items.length === 0 && query.cursor.length === 0,
  );
  const principalPermissions =
    auth.status === 'authenticated' ? auth.principal.permissions : emptyPermissions;
  const permissions = useMemo(() => new Set(principalPermissions), [principalPermissions]);
  const columns = useMemo<readonly GridColDef<CommissionLedgerEntry>[]>(
    () => ledgerColumns(permissions, language, t),
    [language, permissions, t],
  );
  const mockAdjustments =
    adjustmentDataSource === 'mock' && hasMockAdjustments(repository) ? repository : null;

  const resetCursor = (next: Omit<CommissionLedgerListQuery, 'cursor'>) => {
    setCursorHistory(['']);
    setPageIndex(0);
    setQuery({ ...next, cursor: '' });
  };

  const nextPage = () => {
    const nextCursor = resource.data?.nextCursor;
    if (nextCursor === null || nextCursor === undefined) return;
    const nextIndex = pageIndex + 1;
    setCursorHistory((current) => [...current.slice(0, nextIndex), nextCursor]);
    setPageIndex(nextIndex);
    setQuery((current) => ({ ...current, cursor: nextCursor }));
  };

  const previousPage = () => {
    if (pageIndex === 0) return;
    const previousIndex = pageIndex - 1;
    setPageIndex(previousIndex);
    setQuery((current) => ({ ...current, cursor: cursorHistory[previousIndex] ?? '' }));
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <DataSourceBadge source={dataSource} />
            {mockAdjustments === null ? null : (
              <Button onClick={() => setAdjustmentOpen(true)} variant="contained">
                {t('commissionLedger.actions.createAdjustment')}
              </Button>
            )}
          </Stack>
        }
        subtitle={t('commissionLedger.subtitle')}
        title={t('commissionLedger.title')}
      />
      {adjustmentDataSource === 'unavailable' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('commissionLedger.adjustment.unavailable')}
        </Alert>
      ) : null}
      <LedgerFilters
        onApply={(filters, sort) => resetCursor({ pageSize: query.pageSize, sort, filters })}
        value={query}
      />
      <PageState
        emptyMessage={t('commissionLedger.empty')}
        error={toCommissionLedgerPageError(resource.error, t('commissionLedger.errors.load'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={1}>
            {mobile ? (
              <CompactLedgerResults
                entries={resource.data.items}
                language={language}
                permissions={permissions}
              />
            ) : (
              <Box
                aria-label={t('commissionLedger.gridLabel')}
                role="region"
                sx={{ maxWidth: '100%', overflowX: 'auto' }}
                tabIndex={0}
              >
                <Box sx={{ height: 400, minWidth: 1500 }}>
                  <DataGrid
                    aria-label={t('commissionLedger.gridLabel')}
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter
                    rows={resource.data.items}
                    sortingMode="server"
                  />
                </Box>
              </Box>
            )}
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
            >
              <Button disabled={pageIndex === 0} onClick={previousPage}>
                {t('commissionLedger.pagination.previous')}
              </Button>
              <Button disabled={resource.data.nextCursor === null} onClick={nextPage}>
                {t('commissionLedger.pagination.next')}
              </Button>
            </Stack>
          </Stack>
        )}
      </PageState>
      {mockAdjustments === null ? null : (
        <CommissionAdjustmentDialog
          onClose={() => setAdjustmentOpen(false)}
          open={adjustmentOpen}
          repository={mockAdjustments}
        />
      )}
    </Box>
  );
}

const emptyPermissions = Object.freeze<readonly string[]>([]);

function CompactLedgerResults({
  entries,
  language,
  permissions,
}: {
  readonly entries: readonly CommissionLedgerEntry[];
  readonly language: TimeLanguage;
  readonly permissions: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('commissionLedger.compactResultsLabel')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {entries.map((entry) => (
        <Paper component="li" key={entry.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Chip label={entry.entryType} size="small" />
              <Typography sx={{ fontWeight: 700 }}>
                {formatExactSignedMoney(entry.signedAmount, entry.currencyCode)}
              </Typography>
            </Stack>
            <Link to={`${paths.commissionLedger}/${encodeURIComponent(entry.id)}`}>{entry.id}</Link>
            <Typography variant="body2">{beneficiaryValue(entry, permissions)}</Typography>
            <Typography color="text.secondary" variant="caption">
              {formatTimestamp(entry.effectiveAt, 'Asia/Bangkok', language).display}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function LedgerFilters({
  onApply,
  value,
}: {
  readonly onApply: (
    filters: CommissionLedgerFilters,
    sort: CommissionLedgerListQuery['sort'],
  ) => void;
  readonly value: CommissionLedgerListQuery;
}) {
  const { t } = useTranslation();
  const { handleSubmit, register, reset } = useForm<
    CommissionLedgerFilters & { readonly sort: CommissionLedgerListQuery['sort'] }
  >({ defaultValues: { ...value.filters, sort: value.sort } });
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      beneficiaryId: value.filters.beneficiaryId,
      beneficiaryType: value.filters.beneficiaryType,
      currency: value.filters.currency,
      effectiveFrom: value.filters.effectiveFrom,
      effectiveTo: value.filters.effectiveTo,
      maximumAmount: value.filters.maximumAmount,
      minimumAmount: value.filters.minimumAmount,
      partnerTransactionId: value.filters.partnerTransactionId,
      transactionId: value.filters.transactionId,
      sort: value.sort,
    },
    {
      beneficiaryId: defaultCommissionLedgerListQuery.filters.beneficiaryId,
      beneficiaryType: defaultCommissionLedgerListQuery.filters.beneficiaryType,
      currency: defaultCommissionLedgerListQuery.filters.currency,
      effectiveFrom: defaultCommissionLedgerListQuery.filters.effectiveFrom,
      effectiveTo: defaultCommissionLedgerListQuery.filters.effectiveTo,
      maximumAmount: defaultCommissionLedgerListQuery.filters.maximumAmount,
      minimumAmount: defaultCommissionLedgerListQuery.filters.minimumAmount,
      partnerTransactionId: defaultCommissionLedgerListQuery.filters.partnerTransactionId,
      transactionId: defaultCommissionLedgerListQuery.filters.transactionId,
      sort: defaultCommissionLedgerListQuery.sort,
    },
  );
  return (
    <>
      <ListFilterPanel
        activeSecondaryFilterCount={activeSecondaryFilterCount}
        applyLabel={t('commissionLedger.actions.applyFilters')}
        ariaLabel={t('filterPanel.label', { page: t('commissionLedger.title') })}
        onClear={() => {
          reset({
            ...defaultCommissionLedgerListQuery.filters,
            sort: defaultCommissionLedgerListQuery.sort,
          });
          onApply(defaultCommissionLedgerListQuery.filters, defaultCommissionLedgerListQuery.sort);
        }}
        onSubmit={handleSubmit(({ sort, ...filters }) => onApply(normalizeFilters(filters), sort))}
        primaryFields={
          <>
            <TextField
              label={t('commissionLedger.fields.beneficiaryUsername')}
              size="small"
              {...register('beneficiaryUsername')}
            />
            <TextField
              label={t('commissionLedger.fields.partnerId')}
              size="small"
              {...register('partnerId')}
            />
            <TextField
              label={t('commissionLedger.fields.agentId')}
              size="small"
              {...register('agentId')}
            />
            <TextField
              defaultValue=""
              label={t('commissionLedger.fields.entryType')}
              select
              size="small"
              {...register('entryType')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {['EARN', 'REVERSAL', 'ADJUSTMENT'].map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
        secondaryFields={
          <>
            <TextField
              defaultValue=""
              label={t('commissionLedger.fields.beneficiaryType')}
              select
              size="small"
              {...register('beneficiaryType')}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="AGENT">AGENT</MenuItem>
              <MenuItem value="AFFILIATE_MEMBER">AFFILIATE_MEMBER</MenuItem>
            </TextField>
            <TextField
              label={t('commissionLedger.fields.beneficiaryId')}
              size="small"
              {...register('beneficiaryId')}
            />
            <TextField
              label={t('commissionLedger.fields.transactionId')}
              size="small"
              {...register('transactionId')}
            />
            <TextField
              label={t('commissionLedger.fields.partnerTransactionId')}
              size="small"
              {...register('partnerTransactionId')}
            />
            <TextField
              label={t('commissionLedger.fields.currency')}
              size="small"
              slotProps={{ htmlInput: { maxLength: 3 } }}
              {...register('currency')}
            />
            <TextField
              label={t('commissionLedger.filters.minimumAmount')}
              size="small"
              {...register('minimumAmount')}
            />
            <TextField
              label={t('commissionLedger.filters.maximumAmount')}
              size="small"
              {...register('maximumAmount')}
            />
            <TextField
              label={t('commissionLedger.filters.effectiveFrom')}
              size="small"
              type="datetime-local"
              slotProps={{ inputLabel: { shrink: true } }}
              {...register('effectiveFrom')}
            />
            <TextField
              label={t('commissionLedger.filters.effectiveTo')}
              size="small"
              type="datetime-local"
              slotProps={{ inputLabel: { shrink: true } }}
              {...register('effectiveTo')}
            />
            <TextField
              defaultValue={value.sort}
              label={t('commissionLedger.filters.sort')}
              select
              size="small"
              {...register('sort')}
            >
              <MenuItem value="effectiveAt">{t('commissionLedger.fields.effectiveAt')}</MenuItem>
              <MenuItem value="createdAt">{t('commissionLedger.fields.createdAt')}</MenuItem>
            </TextField>
          </>
        }
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('commissionLedger.filters.contractNote')}
      </Alert>
    </>
  );
}

function ledgerColumns(
  permissions: ReadonlySet<string>,
  language: TimeLanguage,
  t: ReturnType<typeof useTranslation>['t'],
): readonly GridColDef<CommissionLedgerEntry>[] {
  return [
    {
      field: 'id',
      headerName: t('commissionLedger.fields.entryId'),
      minWidth: 270,
      sortable: false,
      renderCell: ({ row }) => (
        <Link to={`${paths.commissionLedger}/${encodeURIComponent(row.id)}`}>{row.id}</Link>
      ),
    },
    {
      field: 'entryType',
      headerName: t('commissionLedger.fields.entryType'),
      minWidth: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          color={
            row.entryType === 'EARN' ? 'success' : row.entryType === 'REVERSAL' ? 'warning' : 'info'
          }
          label={row.entryType}
          size="small"
        />
      ),
    },
    {
      field: 'signedAmount',
      headerName: t('commissionLedger.fields.signedAmount'),
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <span>{formatExactSignedMoney(row.signedAmount, row.currencyCode)}</span>
      ),
    },
    {
      field: 'beneficiary',
      headerName: t('commissionLedger.fields.beneficiary'),
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => beneficiaryValue(row, permissions),
    },
    {
      field: 'agent',
      headerName: t('commissionLedger.fields.agent'),
      minWidth: 140,
      sortable: false,
      renderCell: ({ row }) =>
        row.agentId === null ? (
          '—'
        ) : permissions.has('AGENT_VIEW') ? (
          <Link to={`${paths.agents}/${encodeURIComponent(row.agentId)}`}>
            {row.agentCode ?? row.agentId}
          </Link>
        ) : (
          (row.agentCode ?? row.agentId)
        ),
    },
    {
      field: 'partner',
      headerName: t('commissionLedger.fields.partner'),
      minWidth: 150,
      sortable: false,
      valueGetter: (_value, row) => row.partner.code,
    },
    {
      field: 'transaction',
      headerName: t('commissionLedger.fields.transactionId'),
      minWidth: 270,
      sortable: false,
      renderCell: ({ row }) => transactionValue(row, permissions),
    },
    {
      field: 'effectiveAt',
      headerName: t('commissionLedger.fields.effectiveAt'),
      minWidth: 190,
      sortable: false,
      valueGetter: (_value, row) =>
        formatTimestamp(row.effectiveAt, 'Asia/Bangkok', language).display,
    },
    {
      field: 'createdAt',
      headerName: t('commissionLedger.fields.createdAt'),
      minWidth: 190,
      sortable: false,
      valueGetter: (_value, row) =>
        formatTimestamp(row.createdAt, 'Asia/Bangkok', language).display,
    },
  ];
}

function beneficiaryValue(entry: CommissionLedgerEntry, permissions: ReadonlySet<string>) {
  const label = entry.beneficiaryUsername ?? entry.beneficiaryId;
  if (entry.beneficiaryType === 'AFFILIATE_MEMBER' && permissions.has('MEMBER_VIEW'))
    return <Link to={`${paths.members}/${encodeURIComponent(entry.beneficiaryId)}`}>{label}</Link>;
  if (entry.beneficiaryType === 'AGENT' && permissions.has('AGENT_VIEW'))
    return <Link to={`${paths.agents}/${encodeURIComponent(entry.beneficiaryId)}`}>{label}</Link>;
  return label;
}

function transactionValue(entry: CommissionLedgerEntry, permissions: ReadonlySet<string>) {
  const transactionId = entry.source.transactionId;
  if (transactionId === null) return entry.source.partnerTransactionId ?? '—';
  return permissions.has('TRANSACTION_VIEW') ? (
    <Link to={`${paths.transactions}/${encodeURIComponent(transactionId)}`}>{transactionId}</Link>
  ) : (
    transactionId
  );
}

function normalizeFilters(filters: CommissionLedgerFilters): CommissionLedgerFilters {
  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      key,
      key === 'currency' ? value.trim().toUpperCase() : value.trim(),
    ]),
  ) as unknown as CommissionLedgerFilters;
}

function initialQuery(searchParams: URLSearchParams): CommissionLedgerListQuery {
  const filters = { ...defaultCommissionLedgerListQuery.filters };
  for (const key of [
    'partnerId',
    'agentId',
    'beneficiaryId',
    'beneficiaryUsername',
    'transactionId',
    'partnerTransactionId',
    'currency',
  ] as const) {
    filters[key] = searchParams.get(key) ?? '';
  }
  return { ...defaultCommissionLedgerListQuery, filters };
}

export function toCommissionLedgerPageError(
  error: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> | null {
  if (error === null || error === undefined) return null;
  if (typeof error === 'object' && error !== null) {
    const requestId = (error as Partial<AppError>).requestId;
    return { message: fallback, requestId: typeof requestId === 'string' ? requestId : null };
  }
  return { message: fallback, requestId: null };
}
